import React, { Suspense, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import "./App.css";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import PaywallScreen from "./components/PaywallScreen";
import LoginPage from "./pages/LoginPage";

const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const PipelinesPage = React.lazy(() => import("./pages/PipelinesPage"));
const ContactsPage = React.lazy(() => import("./pages/ContactsPage"));
const IntegrationsPage = React.lazy(() => import("./pages/IntegrationsPage"));
const OnboardingPage = React.lazy(() => import("./pages/OnboardingPage"));
const AIHelperPage = React.lazy(() => import("./pages/AIHelperPage"));
const BillingPage = React.lazy(() => import("./pages/BillingPage"));
const BillingConfirmPage = React.lazy(() => import("./pages/BillingConfirmPage"));
const BillingSubscribePage = React.lazy(() => import("./pages/BillingSubscribePage"));
const BillingReturnPage = React.lazy(() => import("./pages/BillingReturnPage"));
const BetaPage = React.lazy(() => import("./pages/BetaPage"));
const BetaRequiredPage = React.lazy(() => import("./pages/BetaRequiredPage"));
const BetaExpiredPage = React.lazy(() => import("./pages/BetaExpiredPage"));
const BetaInvitesPage = React.lazy(() => import("./pages/BetaInvitesPage"));
const JoinPage = React.lazy(() => import("./pages/JoinPage"));
const InviteAcceptPage = React.lazy(() => import("./pages/InviteAcceptPage"));
const WorkspaceMembersPage = React.lazy(() => import("./pages/WorkspaceMembersPage"));
const TodayPage = React.lazy(() => import("./pages/TodayPage"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-700">
      <span className="text-sm font-medium tracking-wide">Loading…</span>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        data-testid="auth-loading-state"
        className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-700"
      >
        <span className="text-sm font-medium tracking-wide">Loading your workspace…</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppShell({ children }) {
  const { user, logout, entitlements, api } = useAuth();
  const [workspaceName, setWorkspaceName] = useState(null);
  
  useEffect(() => {
    if (!api) return;
    api.get("/api/workspaces/me")
      .then(res => setWorkspaceName(res.data?.name || null))
      .catch(() => setWorkspaceName(null));
  }, [api]);
  
  const getPlanDisplay = () => {
    if (!entitlements) return "Solo workspace";
    const planName = entitlements.plan || "solo";
    return `${planName.charAt(0).toUpperCase() + planName.slice(1)} workspace`;
  };

  const isSuperAdmin = user?.email === "hello@theocrm.com";
  
  const canSeeMembers = () => {
    if (isSuperAdmin) return true;
    
    if (!entitlements) return false;
    
    const workspaceRole = (entitlements.workspace_role || "").toLowerCase();
    return workspaceRole === "owner" || workspaceRole === "admin";
  };

  return (
    <div data-testid="app-shell" className="min-h-screen text-slate-900 flex" style={{ backgroundColor: "#F6F8FC" }}>
      <aside
        data-testid="app-sidebar"
        className="fixed left-0 top-0 h-screen flex flex-col overflow-y-auto"
        style={{ 
          width: '220px',
          backgroundColor: '#111B2E',
          padding: '20px 12px',
          color: '#E5E9F2'
        }}
      >
        {/* Logo */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          marginBottom: '6px'
        }}>
          <img 
            src="/theo-monogram-navy-64.png" 
            alt="Theo" 
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              flexShrink: 0
            }}
          />
          <div style={{
            fontSize: '28px',
            fontWeight: '600',
            color: 'white',
            lineHeight: '1'
          }}>
            Theo
          </div>
        </div>

        {/* Tagline - Welcome back with user's first name */}
        <div style={{
          fontSize: '11px',
          color: '#94A3B8',
          marginBottom: '12px',
          lineHeight: '1.4'
        }}>
          Welcome back, {(() => {
            const name = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
            return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
          })()}
        </div>

        {/* Workspace Card */}
        {workspaceName && (
          <div 
            data-testid="sidebar-workspace-card"
            style={{
              backgroundColor: '#0D1526',
              borderRadius: '8px',
              padding: '12px 14px',
              marginBottom: '16px'
            }}>
            <div 
              data-testid="sidebar-workspace-name"
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#FFFFFF',
                marginBottom: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              {workspaceName}
            </div>
            <div 
              data-testid="sidebar-workspace-tier"
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#19B6A2',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
              {(entitlements?.plan || 'solo').toUpperCase()}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav style={{ flex: 1 }}>
          <NavLink to="/app/today" label="Today" testId="nav-today" />
          <NavLink to="/app/dashboard" label="Dashboard" testId="nav-dashboard" />
          <NavLink to="/app/pipelines" label="Pipeline" testId="nav-pipelines" />
          <NavLink to="/app/contacts" label="Contacts" testId="nav-contacts" />
          <NavLink to="/app/ai-helper" label="AI Helper" testId="nav-ai-helper" />
          <NavLink to="/app/settings/integrations" label="Integrations" testId="nav-integrations" />
          <div style={{ height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '12px 0' }} />
          {canSeeMembers() && (
            <NavLink to="/app/settings/members" label="Members & Invites" testId="nav-members" />
          )}
          {isSuperAdmin && (
            <NavLink to="/app/settings/beta-invites" label="Beta Invites" testId="nav-beta-invites" />
          )}
          {canSeeMembers() && (
            <NavLink to="/app/billing" label="Billing" testId="nav-billing" />
          )}
        </nav>

        {/* User Footer */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '220px',
          padding: '12px',
          backgroundColor: '#0B1220',
          color: '#64748B',
          fontSize: '11px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 101
        }}>
          <div style={{
            fontWeight: '500',
            color: '#E5E9F2',
            marginBottom: '2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
            data-testid="sidebar-user-email"
          >
            {user?.email}
          </div>
          <div style={{ marginBottom: '8px', color: '#64748B' }} data-testid="sidebar-plan-display">
            {getPlanDisplay()}
          </div>
          <div
            data-testid="sidebar-logout-button"
            onClick={logout}
            style={{
              color: '#64748B',
              cursor: 'pointer',
              transition: 'color 200ms ease',
              fontSize: '11px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#19B6A2'}
            onMouseLeave={(e) => e.target.style.color = '#64748B'}
          >
            Log out
          </div>
        </div>
      </aside>
      <main
        data-testid="app-main"
        className="flex-1 min-w-0"
        style={{ marginLeft: '220px', backgroundColor: '#F6F8FC' }}
      >
        {children}
      </main>
    </div>
  );
}

function NavLink({ to, label, testId }) {
  const location = useLocation();
  const active = location.pathname.startsWith(to);

  return (
    <Link
      to={to}
      data-testid={`${testId}-link`}
      style={{
        display: 'block',
        padding: '8px 12px',
        marginBottom: '3px',
        borderRadius: '6px',
        fontSize: '13px',
        color: active ? 'white' : '#E5E9F2',
        backgroundColor: active ? '#19B6A2' : 'transparent',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        fontWeight: active ? '500' : '400',
        textDecoration: 'none',
        userSelect: 'none'
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
          e.target.style.color = 'white';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.color = '#E5E9F2';
        }
      }}
    >
      {label}
    </Link>
  );
}

function PrivateApp() {
  return (
    <AuthProvider>
      <PrivateRoute>
        <AppShell>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="today" element={<TodayPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="pipelines" element={<PipelinesPage />} />
              <Route path="pipelines/:pipelineId" element={<PipelinesPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="ai-helper" element={<AIHelperPage />} />
              <Route path="settings/integrations" element={<IntegrationsPage />} />
              <Route path="settings/beta-invites" element={<BetaInvitesPage />} />
              <Route path="settings/members" element={<WorkspaceMembersPage />} />
              <Route path="members-invites" element={<WorkspaceMembersPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="billing/confirm" element={<BillingConfirmPage />} />
              <Route path="billing/subscribe" element={<BillingSubscribePage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="*" element={<Navigate to="today" replace />} />
            </Routes>
          </Suspense>
        </AppShell>
      </PrivateRoute>
      <PaywallScreen />
    </AuthProvider>
  );
}

function App() {
  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* PUBLIC ROUTES - completely outside AuthProvider */}
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/beta" element={<BetaPage />} />
          <Route path="/beta-required" element={<BetaRequiredPage />} />
          <Route path="/beta-expired" element={<BetaExpiredPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/billing/subscribe" element={<BillingSubscribePage />} />
          <Route path="/billing/return" element={<BillingReturnPage />} />
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* PRIVATE ROUTES - wrapped in AuthProvider */}
          <Route path="/app/*" element={<PrivateApp />} />

          {/* Catch-all redirects to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </Router>
  );
}

export default App;
